<template>
  <v-card 
    class="intelligence-clusters-panel mb-6" 
    elevation="0">
    <v-card-title class="pb-2 d-flex justify-space-between align-center">
      <h3 class="panel-title text-h6 font-weight-semibold mb-0">Your Clusters</h3>
      <!-- <v-btn icon @click="handleOpenInNewTab" large>
        <v-icon color="secondary">mdi-open-in-new</v-icon>
      </v-btn> -->
    </v-card-title>

    <!-- Search Bar -->
    <v-card-text v-if="!loading" class="pa-4 pb-2">
      <v-text-field
        v-model="searchQuery"
        placeholder="Search clusters..."
        prepend-inner-icon="mdi-magnify"
        dense
        outlined
        hide-details
        clearable
        style="max-width: 100%;"
        @input="handleSearchChange"
      ></v-text-field>
    </v-card-text>

    <v-card-text class="pa-4">
      <div v-if="loading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
      </div>
      
      <div v-else-if="filteredClusters.length === 0" class="empty-state text-center py-4">
        <p class="empty-state-text text-body-2 mb-2">
          {{ searchQuery ? 'No clusters found matching your search' : 'No clusters configured yet' }}
        </p>
        <p v-if="!searchQuery" class="empty-state-hint text-caption">
          Complete your intelligence setup to create clusters
        </p>
      </div>
      
      <div v-else>
        <v-list class="pa-0">
          <v-list-item
            v-for="cluster in paginatedClusters"
            :key="cluster.id"
            @click="handleClusterClick(cluster.id)"
            class="cluster-item px-0">
            <v-list-item-content>
              <v-list-item-title class="cluster-title text-body-2 font-weight-medium mb-1">
                {{ cluster.name }}
              </v-list-item-title>
              <v-list-item-subtitle class="cluster-subtitle text-caption">
                <v-chip x-small :color="getClusterTypeColor(cluster.cluster_type_id)" dark class="mr-2">
                  {{ getClusterTypeName(cluster.cluster_type_id) }}
                </v-chip>
                {{ cluster.entity_type === 'publisher' ? 'Buyer Cluster' : 'Supplier Cluster' }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon color="grey">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>
    </v-card-text>

    <!-- Pagination -->
    <v-card-actions v-if="!loading && filteredClusters.length > pageSize" class="pagination-container pa-4 pt-2">
      <div class="pagination-wrapper d-flex justify-space-between align-center">
        <div class="pagination-text text-body">
          Showing {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, filteredClusters.length) }} of {{ filteredClusters.length }}
        </div>
        <div class="d-flex gap-2">
          <v-btn
            icon
            x-small
            :disabled="currentPage === 1"
            @click="handlePageChange('prev')"
            class="pagination-button">
            <v-icon small>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            icon
            x-small
            :disabled="currentPage >= totalPages"
            @click="handlePageChange('next')"
            class="pagination-button">
            <v-icon small>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
// Cluster types needed for panel intelligence setup
const REQUIRED_CLUSTER_TYPES = [
  { id: 6, name: 'Target Accounts', label: 'Targets Mapped', entityType: 'publisher', color: 'orange' },
  { id: 2, name: 'Customers', label: 'Customers Mapped', entityType: 'publisher', color: 'green' },
  { id: 3, name: 'Competitors', label: 'Competitors Mapped', entityType: 'supplier', color: 'red' },
  { id: 1, name: 'Partners', label: 'Partners Mapped', entityType: 'supplier', color: 'blue' }
];

export default {
  name: 'IntelligenceClustersPanel',
  
  props: {
    clusters: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 10
    }
  },

  data() {
    return {
      searchQuery: ''
    };
  },

  computed: {
    filteredClusters() {
      if (!this.searchQuery) {
        return this.clusters;
      }
      const query = this.searchQuery.toLowerCase();
      return this.clusters.filter(cluster => {
        const name = (cluster.name || '').toLowerCase();
        return name.includes(query);
      });
    },

    totalPages() {
      return Math.ceil(this.filteredClusters.length / this.pageSize);
    },

    paginatedClusters() {
      if (!this.filteredClusters) return [];
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredClusters.slice(start, end);
    }
  },

  watch: {
    searchQuery() {
      // Reset to first page when search query changes
      this.$emit('page-change', 1);
    }
  },

  methods: {
    getClusterTypeName(clusterTypeId) {
      const clusterType = REQUIRED_CLUSTER_TYPES.find(t => t.id === clusterTypeId);
      return clusterType ? clusterType.name : 'Unknown';
    },

    getClusterTypeColor(clusterTypeId) {
      const clusterType = REQUIRED_CLUSTER_TYPES.find(t => t.id === clusterTypeId);
      return clusterType ? clusterType.color : 'grey';
    },

    handleClusterClick(clusterId) {
      this.$emit('cluster-click', clusterId);
    },

    handleSearchChange() {
      // Emit search change event if needed
      this.$emit('search-change', this.searchQuery);
    },

    handlePageChange(direction) {
      const newPage = direction === 'next' ? this.currentPage + 1 : this.currentPage - 1;
      this.$emit('page-change', newPage);
    },

    handleOpenInNewTab() {
      this.$emit('open-in-new-tab', { name: 'clusters' });
    }
  }
};
</script>

<style scoped>
/* Card Container */
.intelligence-clusters-panel {
  border: 1px solid #e5e7eb;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

/* Title */
.panel-title {
  color: #1f2937;
}

/* Empty State */
.empty-state-text {
  color: #6b7280;
}

.empty-state-hint {
  color: #9ca3af;
}

/* Cluster Items */
.cluster-item {
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
}

.cluster-title {
  color: #1f2937;
}

.cluster-subtitle {
  color: #6b7280;
}

/* Pagination */
.pagination-container {
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.pagination-wrapper {
  width: 100%;
}

.pagination-text {
  color: #6b7280;
}

.pagination-button {
  color: #6b7280;
}

.gap-2 {
  gap: 8px;
}
</style>